import { useTranslation } from "react-i18next";
import { Lock, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { formatKickoff, isLocked } from "@/lib/utils-app";
import { TeamFlag } from "./TeamFlag";

export interface MatchData {
  id: string;
  phase: string;
  group_name: string | null;
  kickoff_at: string;
  home_team: string;
  away_team: string;
  home_score: number | null;
  away_score: number | null;
  match_number?: number | null;
  stadium?: string | null;
  city?: string | null;
  tournament_id?: string | null;
}

interface Props {
  match: MatchData;
  right?: React.ReactNode;
}

/** Pure presentational match card — mobile-first, RN-portable. */
export const MatchCard = ({ match, right }: Props) => {
  const { t } = useTranslation();
  const finished = match.home_score !== null && match.away_score !== null;
  const locked = isLocked(match.kickoff_at);
  const venue = [match.stadium, match.city].filter(Boolean).join(", ");
  return (
    <Card>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          {/* Home */}
          <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
            <span className="font-semibold text-sm truncate">{match.home_team}</span>
            <TeamFlag team={match.home_team} size="md" />
          </div>

          {/* Center: score or time */}
          <div className="min-w-[68px] text-center">
            {finished ? (
              <span className="font-bold text-lg tabular-nums">{match.home_score}:{match.away_score}</span>
            ) : (
              <span className="text-base font-bold tabular-nums whitespace-nowrap">
                {new Date(match.kickoff_at).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>

          {/* Away */}
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <TeamFlag team={match.away_team} size="md" />
            <span className="font-semibold text-sm truncate">{match.away_team}</span>
          </div>

          {right ?? (locked && !finished && <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />)}
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
          {match.match_number != null && (
            <span className="font-medium text-foreground/70">#{match.match_number}</span>
          )}
          {match.group_name && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              {t("phases.groupShort")} {match.group_name}
            </Badge>
          )}
          <span className="whitespace-nowrap">{formatKickoff(match.kickoff_at)}</span>
          {venue && (
            <span className="flex items-center gap-1 min-w-0">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{venue}</span>
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
